import { useState, useEffect, useRef } from "react";
import { SxProps, TextField, Box, InputAdornment, CircularProgress } from "@mui/material";
import { CheckCircle, Assistant } from "@mui/icons-material";
import { AvailabilityRequestDto } from "../../../api-client";


interface AIAvailabilityProps {
    request: AvailabilityRequestDto;
    label?: string;
    onChange?: (requestString: string) => void;
    onSubmit?: (requestString: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    disabled?: boolean;
    loading?: boolean;
    sx?: SxProps;
}

const AIAvailabilityTextField: React.FC<AIAvailabilityProps> = (props) => {
    const { request, label, onChange, onSubmit, onBlur, placeholder, disabled, loading, ...sx } = props;
    const [localValue, setLocalValue] = useState(request.requestString || "");
    const justSubmittedRef = useRef(false);

    useEffect(() => {
        setLocalValue(request.requestString || "");
    }, [request.requestString]);

    // Handler for Enter key
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault(); // Prevent form submission
            const trimmedValue = localValue.trim();
            if (trimmedValue && onSubmit) {
                onSubmit(trimmedValue);
            }
            justSubmittedRef.current = true;
            // Blur the field so the user sees the action is complete
            (e.target as HTMLInputElement).blur();
        }
    };

    const handleBlur = () => {
        if (justSubmittedRef.current) {
            justSubmittedRef.current = false;
        }
        if (onBlur) {
            onBlur();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value);
    };

    return (
        <Box display="flex" flexDirection="column" sx={{ width: "100%" }}>
            <TextField
                label={label}
                value={localValue}
                placeholder={placeholder}
                // multiline 
                fullWidth
                disabled={disabled}
                sx={{ 
                    outline: "none", 
                    "& .MuiInputBase-root": 
                        { pl: 1 } 
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Assistant />
                        </InputAdornment>
                    ),
                    endAdornment: loading ? (
                        <InputAdornment position="end">
                            <CircularProgress color="inherit" size={20} />
                        </InputAdornment>
                    ) : undefined,
                }}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
            />
        </Box>
    );
}

export default AIAvailabilityTextField;